<template>
  <div class="v2026-sp-anchor" @keydown.esc="onEsc" @focusout="onFocusOut">
    <!-- Same class as the b-dropdown toggle it replaces, so the closed box stays pixel-identical. -->
    <button type="button"
            ref="triggerBtn"
            class="v2026-emoji"
            aria-haspopup="grid"
            aria-label="สติกเกอร์"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="panelId"
            @click.prevent="toggle"
            @keydown.up.prevent="onTriggerArrow">
      <span class="v2026-icon-btn__svg" aria-hidden="true"
            :style="{ '-webkit-mask-image': 'url(' + leadIcon + ')', 'mask-image': 'url(' + leadIcon + ')' }"></span>
      <i class="fas fa-chevron-up v2026-chev" aria-hidden="true"></i>
    </button>

    <!-- :renderKey + the scoped slot's :data-rk are one fix: without both, Vue 2.7 treats the
         slot as stable and freezes the tabs and cells. -->
    <OverlayCard :id="panelId"
                 class="v2026-sp-pos"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 role="region"
                 aria-label="สติกเกอร์"
                 @close="hide()">
      <template #default="rk">
        <div v-if="packageCount"
             class="v2026-sp-rail"
             role="tablist"
             aria-label="ชุดสติกเกอร์"
             :data-rk="rk.dirty">
          <button v-for="(pkg, i) in (packages || [])"
                  :key="pkg.package_id"
                  ref="tabBtn"
                  type="button"
                  class="v2026-sp-tab"
                  role="tab"
                  :aria-selected="pkg.package_id === selectedPackageId ? 'true' : 'false'"
                  :tabindex="pkg.package_id === selectedPackageId ? 0 : -1"
                  :aria-label="'ชุดสติกเกอร์ ' + pkg.package_id"
                  @click="onTab(pkg.package_id)"
                  @keydown="onTabKeydown($event, i)">
            <img :src="pkg.url_img" class="v2026-sp-tab__img" alt="">
          </button>
        </div>

        <div ref="grid"
             class="v2026-sp-grid"
             role="grid"
             aria-label="สติกเกอร์"
             :data-rk="rk.dirty">
          <div class="v2026-sp-gridinner">
            <!-- v-show, never v-if: every package stays downloaded, which is what makes tab
                 switching instant. -->
            <button v-for="cell in cells"
                    v-show="cell.visible"
                    :key="cell.key"
                    type="button"
                    class="v2026-sp-cell"
                    :class="{ 'is-roved': cell.vIdx === rovedIndex }"
                    :tabindex="cell.vIdx === rovedIndex ? 0 : -1"
                    :aria-label="'สติกเกอร์ ' + cell.item.sticker_id"
                    @click.prevent="onPick(cell.item)"
                    @keydown="onCellKeydown($event, cell)">
              <img :src="cell.item.url_img" alt="">
            </button>
          </div>
          <div v-if="!visibleCount" class="v2026-sp-empty">
            {{ packageCount ? 'ไม่มีสติกเกอร์ในชุดนี้' : 'ยังไม่มีชุดสติกเกอร์' }}
          </div>
        </div>
      </template>
    </OverlayCard>
  </div>
</template>

<script>
import OverlayCard from '@/components/coversation/OverlayCard'

export default {
  name: 'sticker-picker-v2026',
  components: {
    OverlayCard
  },
  props: {
    packages: {
      type: Array,
      default: null
    },
    stickers: {
      type: Array,
      default: null
    },
    selectedPackageId: {
      type: String,
      default: null
    },
    leadIcon: {
      type: String,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      open: false,
      rovedIndex: 0,
      triggerEl: null
    }
  },
  computed: {
    panelId () {
      return 'v2026-sp-card'
    },
    packageCount () {
      return (this.packages || []).length
    },
    cells () {
      let v = 0
      return (this.stickers || []).map(item => {
        const visible = item.package_id === this.selectedPackageId
        return {
          // tb_sticker_line holds duplicate (package_id, sticker_id) pairs, so only the row id is
          // unique. Never key by the roved index — that re-creates the <img> on every arrow press.
          key: String(item.id != null ? item.id : item.package_id + '_' + item.sticker_id),
          item,
          visible,
          vIdx: visible ? v++ : -1
        }
      })
    },
    visibleCount () {
      return this.cells.filter(c => c.visible).length
    },
    renderKey () {
      return String(this.selectedPackageId) +
        '#' + (this.open ? 1 : 0) +
        '#' + this.rovedIndex +
        '#' + (this.packages || []).map(p => p.package_id).join(',') +
        '#' + (this.stickers || []).length
    }
  },
  watch: {
    conversationId () {
      this.hide()
    },
    selectedPackageId () {
      this.rovedIndex = 0
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    show () {
      if (this.open) return
      this.open = true
      this.rovedIndex = 0
      this.$emit('open')
      this.$nextTick(() => this.focusActiveTab())
    },
    // Same shape as b-dropdown.hide(refocus), so the frozen selectSticker call site still works.
    hide (returnFocus = false) {
      const wasOpen = this.open
      this.open = false
      this.rovedIndex = 0
      if (wasOpen) this.$emit('close')
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    toggle () {
      if (this.open) this.hide(true)
      else this.show()
    },
    onTriggerArrow () {
      if (!this.open) this.show()
    },
    visibleCellEls () {
      const grid = this.$refs.grid
      if (!grid) return []
      return Array.prototype.slice.call(grid.querySelectorAll('.v2026-sp-cell'))
        .filter(el => el.offsetParent !== null)
    },
    // The grid wraps, so the column count is measured from the rendered rows rather than fixed.
    columnCount (els) {
      if (!els.length) return 1
      const top = els[0].offsetTop
      let cols = 0
      for (const el of els) {
        if (el.offsetTop !== top) break
        cols++
      }
      return Math.max(1, cols)
    },
    focusActiveTab () {
      const tabs = this.$refs.tabBtn
      if (!tabs || !tabs.length) return
      const i = (this.packages || []).findIndex(p => p.package_id === this.selectedPackageId)
      const el = tabs[i === -1 ? 0 : i]
      if (el) el.focus()
    },
    moveRoved (index) {
      const els = this.visibleCellEls()
      if (!els.length) return
      this.rovedIndex = Math.max(0, Math.min(els.length - 1, index))
      this.$nextTick(() => {
        const next = this.visibleCellEls()[this.rovedIndex]
        if (next) {
          next.focus()
          next.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    onTabKeydown (e, i) {
      const list = this.packages || []
      if (!list.length) return
      let next = null
      if (e.key === 'ArrowRight') next = i + 1
      else if (e.key === 'ArrowLeft') next = i - 1
      else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = list.length - 1
      if (next === null) return
      e.preventDefault()
      next = Math.max(0, Math.min(list.length - 1, next))
      this.onTab(list[next].package_id)
      this.$nextTick(() => {
        const tabs = this.$refs.tabBtn
        if (tabs && tabs[next]) tabs[next].focus()
      })
    },
    onCellKeydown (e, cell) {
      const key = e.key
      if (key === 'Enter' || key === ' ') {
        // preventDefault BEFORE the repeat bail-out, or a held key sneaks a second send through
        // the browser's own click synthesis.
        e.preventDefault()
        if (e.repeat) return
        this.onPick(cell.item)
        return
      }
      const els = this.visibleCellEls()
      if (!els.length) return
      const cols = this.columnCount(els)
      let next = null
      if (key === 'ArrowRight') next = cell.vIdx + 1
      else if (key === 'ArrowLeft') next = cell.vIdx - 1
      else if (key === 'ArrowDown') next = cell.vIdx + cols
      else if (key === 'ArrowUp') next = cell.vIdx - cols
      else if (key === 'Home') next = 0
      else if (key === 'End') next = els.length - 1
      if (next === null) return
      e.preventDefault()
      this.moveRoved(next)
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.hide(true)
    },
    onFocusOut (e) {
      if (!this.open) return
      // null relatedTarget = the window lost focus (Alt-Tab). Outside clicks are OverlayCard's job.
      if (!e.relatedTarget) return
      if (this.$el.contains(e.relatedTarget)) return
      this.hide()
    },
    onTab (packageId) {
      this.$emit('select-package', packageId)
    },
    // IRREVERSIBLE: the owner POSTs the sticker to the customer immediately. The open check is a
    // one-shot latch so a second activation cannot produce a second send.
    onPick (sticker) {
      if (!this.open) return
      this.$emit('select', sticker)
    }
  }
}
</script>

<style scoped>
.v2026-sp-anchor {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex-shrink: 0;
}

.v2026-sp-anchor .v2026-emoji {
  border-radius: 0.25rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
  background-image: none;
  transition: background-color 150ms ease, color 150ms ease;
}

.v2026-sp-anchor .v2026-emoji:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-chev {
  font-size: 10px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-icon-btn__svg {
  width: 18px;
  height: 18px;
  display: inline-block;
  flex-shrink: 0;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-sp-anchor .v2026-sp-pos {
  bottom: calc(100% + var(--v2026-sp-2, 8px));
  left: 0;
  width: 490px;
  min-width: 0;
  max-width: calc(100vw - 24px);
  max-height: min(60vh, 520px);
  padding: var(--v2026-sp-2, 8px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.v2026-sp-rail {
  flex: 0 0 auto;
  display: flex;
  gap: var(--v2026-sp-1, 4px);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--v2026-border, #E4E7EC);
  margin-bottom: var(--v2026-sp-2, 8px);
}

.v2026-sp-rail::-webkit-scrollbar {
  height: 6px;
}

.v2026-sp-rail::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main, #31C1D7);
  border-radius: 3px;
}

.v2026-sp-tab {
  position: relative;
  flex: 0 0 auto;
  padding: 4px 6px 6px;
  border: 0;
  background: transparent;
  border-radius: var(--v2026-r-btn, 10px);
  cursor: pointer;
  transition: background-color 150ms ease, opacity 150ms ease;
}

.v2026-sp-tab__img {
  display: block;
  height: 50px;
  width: auto;
  opacity: 0.55;
}

.v2026-sp-tab[aria-selected="true"] .v2026-sp-tab__img {
  opacity: 1;
}

.v2026-sp-tab[aria-selected="true"] {
  background: var(--v2026-color-main-soft, #31C1D714);
}

.v2026-sp-tab[aria-selected="true"]::after {
  content: "";
  position: absolute;
  left: 6px;
  right: 6px;
  bottom: 0;
  height: 2px;
  border-radius: 1px;
  background: var(--v2026-color-main, #31C1D7);
}

.v2026-sp-tab:hover .v2026-sp-tab__img {
  opacity: 0.85;
}

.v2026-sp-tab:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(49, 193, 215, 0.55);
}

.v2026-sp-grid {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 280px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.v2026-sp-grid::-webkit-scrollbar {
  width: 6px;
}

.v2026-sp-grid::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main, #31C1D7);
  border-radius: 3px;
}

.v2026-sp-gridinner {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-content: flex-start;
}

.v2026-sp-cell {
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  border-radius: var(--v2026-r-btn, 10px);
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-sp-cell img {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.v2026-sp-cell:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
}

.v2026-sp-cell:active {
  background: #DDE1E7;
}

.v2026-sp-cell:focus-visible,
.v2026-sp-cell.is-roved {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(49, 193, 215, 0.55);
  background: var(--v2026-color-main-soft, #31C1D714);
}

.v2026-sp-empty {
  padding: var(--v2026-sp-3, 12px);
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: var(--v2026-text-muted, #98A2B3);
}

@media (max-height: 420px) {
  .v2026-sp-tab__img {
    height: 36px;
  }

  .v2026-sp-cell {
    width: 56px;
    height: 56px;
  }

  .v2026-sp-cell img {
    width: 48px;
    height: 48px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .v2026-sp-anchor .v2026-emoji,
  .v2026-sp-tab,
  .v2026-sp-cell {
    transition: none;
  }
}
</style>
